/**
 * Widget Chatbot UTN - React Entry Point
 *
 * This is the main entry point for the React-based chatbot widget.
 * It initializes the application and renders it into the Shadow DOM.
 */

import { createRoot } from 'react-dom/client';
import { StrictMode } from 'react';

// CSS is imported and injected via StyleInjector component using ?inline
// This prevents duplicate CSS loading and ensures proper Shadow DOM encapsulation

// Initialize Sentry for error tracking
import { addSentryBreadcrumb, initializeSentry, setSentryUser } from '@/utils/sentryConfig';

if (import.meta.env.PROD) {
  initializeSentry();
}

// Import App component
import { App } from './App';

// Import utilities
import { initializeChatbotSession } from '@/utils/sessionInitializer';
import { getTokenUserFromCookieChatbot } from '@/utils/chatbotGetToken';
import { getPortalId } from '@/utils/getChatbotEnable';
import { getHasPhoneNumberInfo } from '@/utils/getPhoneNumber';
import { loadGoogleAnalytics } from '@/utils/loadAnalytics';
import { CHATBOT_TOKEN_STRING } from '@/utils/constants';
import { useChatStore } from '@/store/chatStore';

// Verbose logging flag
const verboseChatbot = import.meta.env.VITE_APP_VERBOSE === 'true';

// Clear any stale session token
window.sessionStorage.removeItem(CHATBOT_TOKEN_STRING);

// Initialize chatbot session and get UUID
const { socketChatbotUuid: initialUuid, authStateCheck } = initializeChatbotSession(verboseChatbot);

// Add verbose logging for history mode status
if (verboseChatbot) {
  const historyMode = import.meta.env.VITE_CONVERSATION_HISTORY_MODE || 'enabled';
  console.log('📋 Conversation History Mode:', historyMode);
  if (historyMode === 'disabled') {
    console.log('🔄 Fresh UUID will be generated on each page load');
    console.log('🚫 History will not be loaded from server');
  }
}

// Make UUID available in global scope for debugging (only if verbose mode is enabled)
if (verboseChatbot) {
  (window as any).getChatbotUuid = () => {
    console.log('💡 Current Chatbot UUID Status:');
    console.log('   🔑 Full UUID:', initialUuid);
    console.log('   📝 Base UUID (shared across tabs):', initialUuid.replace(/ - \d+$/, ''));
    console.log('   🏷️  Session Index:', initialUuid.split(' - ')[1] || '0');
    console.log('   💾 localStorage UUID:', localStorage.getItem('uuid'));
    return initialUuid;
  };
}

// Handle auth state change - clear messages if user changed
if (authStateCheck.changed) {
  if (verboseChatbot) {
    console.log('🧹 Auth state changed - clearing chat state');
    console.log('🔍 Auth change event:', authStateCheck.event);
    console.log('🔍 New UUID:', initialUuid);
  }

  // Clear the store state
  useChatStore.getState().clearAuthState();
}

// Check for chatbot-mode query parameter
const urlParams = new URLSearchParams(window.location.search);
const valueQueryParam = urlParams.get('chatbot-mode');
if (valueQueryParam === 'recommender') {
  useChatStore.getState().setNavigationState(1);
  useChatStore.getState().setIsOpen(true);
} else if (valueQueryParam === 'chatbot') {
  useChatStore.getState().setNavigationState(0);
  useChatStore.getState().setIsOpen(true);
}

/**
 * Main initialization function
 * Loads user data, initializes analytics, and renders the app
 */
async function initializeAndRender() {
  // Load token if it exists
  const tokenUser = getTokenUserFromCookieChatbot();
  if (tokenUser) {
    useChatStore.getState().setToken(tokenUser);

    if (import.meta.env.PROD) {
      setSentryUser(tokenUser, {
        authenticated: true,
      });

      addSentryBreadcrumb('User authenticated', 'auth', 'info', {
        userId: tokenUser,
      });
    }

    // Fetch phone number info
    try {
      const hasPhoneNumber = await getHasPhoneNumberInfo(tokenUser);
      useChatStore.getState().setHasPhoneNumber(hasPhoneNumber);
    } catch (error) {
      console.error('Error fetching phone number information:', error);
      useChatStore.getState().setHasPhoneNumber(false);
    }
  }

  // Initialize Google Analytics
  loadGoogleAnalytics();

  // Wait a bit for the page to settle
  await new Promise((resolve) => setTimeout(resolve, 1300));

  // Get portal ID
  const portalId = await getPortalId();
  useChatStore.getState().setPortalId(portalId);

  // Get the Shadow DOM container
  const embeddedComponent = document.getElementById('embedded-component');
  if (!embeddedComponent || !embeddedComponent.shadowRoot) {
    console.error('Widget Chatbot UTN: Could not find embedded-component or shadowRoot');
    return;
  }

  const toRender = embeddedComponent.shadowRoot.getElementById('bot-utn-chatbot-utn-app');
  if (!toRender) {
    console.error('Widget Chatbot UTN: Could not find bot-utn-chatbot-utn-app element');
    return;
  }

  // Create React root and render
  const root = createRoot(toRender);
  root.render(
    <StrictMode>
      <App initialUuid={initialUuid} />
    </StrictMode>
  );

  if (verboseChatbot) {
    console.info('🎉 Widget Chatbot UTN: Component successfully rendered and ready!');
  }
}

// Start the initialization
initializeAndRender().catch((error) => {
  console.error('Widget Chatbot UTN: Initialization failed', error);
});

